/**
 * Deals Kanban Types
 *
 * Story 3.5.5: Deals Kanban Page
 *
 * Types for the Kanban board, deal detail page, and collaboration features.
 */
import { z } from 'zod';
import { DealStageSchema } from './client.js';
import { FORECAST_CATEGORIES } from './forecast.js';
import { MeetingSessionSchema } from './meeting-detail.js';
import { DEAL_MATCH_STRATEGIES } from './deal-match-strategy.js';
// =============================================================================
// Deal Stages - re-exported from client.ts (single source of truth)
// =============================================================================
export { DealStageSchema };
// =============================================================================
// Deal close outcome (Won/Lost within a terminal stage)
// =============================================================================
export const DealCloseOutcome = {
    WON: 'won',
    LOST: 'lost',
};
export const DealCloseOutcomeSchema = z.enum(Object.values(DealCloseOutcome));
// Display metadata for UI. Record<DealCloseOutcome, ...> so a new outcome is a
// compile error until it gets a label here.
export const DealCloseOutcomeMetadata = {
    won: { label: 'Won' },
    lost: { label: 'Lost' },
};
// =============================================================================
// Deal Health
// =============================================================================
export const DealHealth = {
    HEALTHY: 'healthy',
    STALLED: 'stalled',
    AT_RISK: 'at_risk',
};
export const DealHealthSchema = z.enum(Object.values(DealHealth));
// Display metadata for UI. Record<DealHealth, ...> so a new health value is a
// compile error until it gets a label here.
export const DealHealthMetadata = {
    healthy: { label: 'Healthy' },
    stalled: { label: 'Stalled' },
    at_risk: { label: 'At Risk' },
};
// =============================================================================
// Deal creation source
// =============================================================================
// How the deal was created: a deal is created either by a person in the app or by an integration.
export const DealCreationSource = {
    MANUAL: 'manual',
    CRM_SYNC: 'crm_sync',
    CHAT: 'chat',
    CSV_IMPORT: 'csv_import',
};
export const DealCreationSourceSchema = z.enum(Object.values(DealCreationSource));
// Display metadata for UI. Record<DealCreationSource, ...> so a new source is a
// compile error until it gets a label here.
export const DealCreationSourceMetadata = {
    manual: { label: 'Manual' },
    crm_sync: { label: 'CRM Sync' },
    chat: { label: 'Chat' },
    csv_import: { label: 'CSV Import' },
};
export const DealCardDataSchema = z.object({
    id: z.string().uuid(),
    title: z.string(),
    value: z.number().nullable(),
    client: z.object({
        id: z.string().uuid(),
        name: z.string(),
        logo: z.string().url().nullable().optional(),
    }),
    collaborators: z.array(z.object({
        userId: z.string().uuid(),
        name: z.string(),
        profilePictureUrl: z.string().url().nullable().optional(),
        role: z.enum(['owner', 'collaborator']),
    })),
    stage: DealStageSchema,
    health: DealHealthSchema,
    probability: z.number().min(0).max(100).nullable().optional(),
    expectedCloseDate: z.string().nullable().optional(),
    nextMeetingAt: z
        .string()
        .datetime({ offset: true })
        .or(z.string().datetime())
        .nullable()
        .optional(),
    daysInStage: z.number().optional(),
    isOwner: z.boolean(),
    isCollaborator: z.boolean(),
    closeOutcome: DealCloseOutcomeSchema.nullable().optional(),
    closedAt: z.string().datetime({ offset: true }).or(z.string().datetime()).nullable().optional(),
    stageSubdivision: z.string().nullable().optional(),
    subdivision: z.string().nullable().optional(),
    archivedAt: z.string().datetime({ offset: true }).or(z.string().datetime()).nullable().optional(),
    deletedAt: z.string().datetime({ offset: true }).or(z.string().datetime()).nullable().optional(),
    archiveReason: z.string().nullable().optional(),
    lifecyclePreviousState: z.enum(['open', 'closed', 'archived']).nullable().optional(),
    gateProgress: z
        .object({ completed: z.number().int().nonnegative(), total: z.number().int().nonnegative() })
        .optional(),
    qualProgress: z
        .object({ captured: z.number().int().nonnegative(), total: z.number().int().nonnegative() })
        .optional(),
    derivedHealth: z
        .object({
        score: z.enum(['healthy', 'at-risk', 'critical']),
        reasons: z.array(z.string()),
    })
        .nullable()
        .optional(),
});
export const DealStageColumnSchema = z.object({
    stage: DealStageSchema,
    label: z.string(),
    color: z.string(),
    deals: z.array(DealCardDataSchema),
    totalValue: z.number(),
    dealCount: z.number(),
});
export const PipelineStatsSchema = z.object({
    totalPipeline: z.number(),
    forecast: z.number(),
    winRate: z.number(),
    avgCycleDays: z.number(),
    percentChange: z
        .object({
        pipeline: z.number().optional(),
        forecast: z.number().optional(),
        winRate: z.number().optional(),
        cycleDays: z.number().optional(),
    })
        .optional(),
});
export const StageTransitionSchema = z.object({
    id: z.string().uuid(),
    orgId: z.string().min(1),
    dealId: z.string().uuid(),
    fromStage: z.string().nullable(),
    toStage: z.string(),
    fromProbability: z.number().min(0).max(100).nullable(),
    toProbability: z.number().min(0).max(100).nullable(),
    changedBy: z.string().nullable(),
    source: z.string(),
    reason: z.string().nullable(),
    evidenceType: z.enum(['email', 'meeting']).nullable().optional(),
    evidenceId: z.string().nullable().optional(),
    changedAt: z.string().datetime(),
});
export const StageDurationSchema = z.object({
    stage: z.string(),
    avgDays: z.number(),
    count: z.number(),
});
export const FunnelStageConversionSchema = z.object({
    stage: z.string(),
    reached: z.number(),
    converted: z.number(),
    conversionPct: z.number(),
    leakagePct: z.number(),
});
export const FunnelConversionResponseSchema = z.object({
    stages: z.array(FunnelStageConversionSchema),
});
export const PipelineMovementSchema = z.object({
    startValue: z.number(),
    created: z.number(),
    increased: z.number(),
    decreased: z.number(),
    won: z.number(),
    lost: z.number(),
    slipped: z.number(),
    endValue: z.number(),
});
export const WinLossBreakdownRowSchema = z.object({
    key: z.string(),
    label: z.string(),
    wonCount: z.number(),
    lostCount: z.number(),
    wonValue: z.number(),
    lostValue: z.number(),
    winRate: z.number(),
});
export const WinLossBreakdownSchema = z.object({
    byReason: z.array(WinLossBreakdownRowSchema),
    byRep: z.array(WinLossBreakdownRowSchema),
    byStage: z.array(WinLossBreakdownRowSchema),
    totals: z.object({
        wonCount: z.number(),
        lostCount: z.number(),
        wonValue: z.number(),
        lostValue: z.number(),
        winRate: z.number(),
    }),
});
export const DealsKanbanResponseSchema = z.object({
    columns: z.array(DealStageColumnSchema),
    stats: PipelineStatsSchema,
});
export const DealDetailSchema = z.object({
    id: z.string().uuid(),
    title: z.string(),
    value: z.number().nullable(),
    stage: DealStageSchema,
    health: DealHealthSchema,
    probability: z.number().nullable(),
    expectedCloseDate: z.string().nullable(),
    daysInStage: z.number(),
    closeOutcome: DealCloseOutcomeSchema.nullable().optional(),
    closedAt: z.string().datetime({ offset: true }).or(z.string().datetime()).nullable().optional(),
    stageSubdivision: z.string().nullable().optional(),
    stageReason: z.string().nullable().optional(),
    stageNotes: z.string().nullable().optional(),
    subdivision: z.string().nullable().optional(),
    forecastCategory: z.enum(FORECAST_CATEGORIES).nullable().optional(),
    effectiveForecastCategory: z.enum(FORECAST_CATEGORIES).optional(),
    archivedAt: z.string().datetime({ offset: true }).or(z.string().datetime()).nullable().optional(),
    deletedAt: z.string().datetime({ offset: true }).or(z.string().datetime()).nullable().optional(),
    archiveReason: z.string().nullable().optional(),
    lifecyclePreviousState: z.enum(['open', 'closed', 'archived']).nullable().optional(),
    createdAt: z.string().datetime({ offset: true }).or(z.string().datetime()),
    updatedAt: z.string().datetime({ offset: true }).or(z.string().datetime()),
    client: z.object({
        id: z.string().uuid(),
        name: z.string(),
        logo: z.string().url().nullable().optional(),
        domain: z.string().nullable().optional(),
    }),
    owner: z.object({
        userId: z.string().uuid().nullable(),
        name: z.string(),
        email: z.string().email().optional(),
        profilePictureUrl: z.string().url().nullable().optional(),
        orgRole: z.enum(['admin', 'sales-rep']).optional(),
        isDeleted: z.boolean().optional(),
    }),
    createdBy: z
        .object({
        userId: z.string().uuid().nullable(),
        name: z.string(),
        email: z.string().email().optional(),
        profilePictureUrl: z.string().url().nullable().optional(),
        orgRole: z.enum(['admin', 'sales-rep']).optional(),
        isDeleted: z.boolean().optional(),
    })
        .nullable(),
    source: DealCreationSourceSchema,
    contacts: z.array(z.object({
        id: z.string().uuid(),
        name: z.string(),
        email: z.string().email().nullable().optional(),
        role: z.string().nullable().optional(),
        title: z.string().nullable().optional(),
        avatar: z.string().url().nullable().optional(),
    })),
    customFields: z.record(z.string(), z.unknown()).optional(),
});
// =============================================================================
// Activity Timeline
// =============================================================================
export const TimelineItemType = {
    MEETING: 'meeting',
    EMAIL: 'email',
    NOTE: 'note',
    STAGE_CHANGE: 'stage_change',
    TASK: 'task',
};
export const TimelineItemTypeSchema = z.enum(Object.values(TimelineItemType));
// Timeline filter for activity filtering
export const TimelineFilter = {
    ALL: 'all',
    EMAILS: 'emails',
    MEETINGS: 'meetings',
};
export const TimelineFilterSchema = z.enum(Object.values(TimelineFilter));
export const TimelineItemSchema = z.object({
    id: z.string(),
    type: TimelineItemTypeSchema,
    title: z.string(),
    timestamp: z.string().datetime(),
    ownerUserId: z.string().uuid().nullable().optional(),
    description: z.string().nullable().optional(),
    subtitle: z.string().optional(),
    duration: z.string().optional(),
    hasRecording: z.boolean().optional(),
    preview: z.string().optional(),
    scheduledStart: z.string().nullable().optional(),
    scheduledEnd: z.string().nullable().optional(),
    durationMs: z.number().int().nullable().optional(),
    sessionCount: z.number().int().min(0).optional(),
    sessions: z.array(MeetingSessionSchema),
    isAllDay: z.boolean().optional(),
    dealMatchStrategy: z.enum(DEAL_MATCH_STRATEGIES).nullable().optional(),
    dealConfidenceScore: z.number().min(0).max(100).nullable().optional(),
    dealLinkReason: z.string().nullable().optional(),
    metadata: z
        .object({
        duration: z.number().optional(),
        participants: z.array(z.string()).optional(),
        recordingUrl: z.string().url().optional(),
        subject: z.string().optional(),
        from: z.string().optional(),
        to: z.array(z.string()).optional(),
        threadId: z.string().optional(),
        fromStage: DealStageSchema.optional(),
        toStage: DealStageSchema.optional(),
        changedBy: z.string().optional(),
        author: z.string().optional(),
        dueDate: z.string().datetime().optional(),
        completed: z.boolean().optional(),
    })
        .nullable()
        .optional(),
});
// =============================================================================
// Collaborators
// =============================================================================
export const CollaboratorRole = {
    OWNER: 'owner',
    COLLABORATOR: 'collaborator',
};
export const CollaboratorStatus = {
    ACCEPTED: 'accepted',
    PENDING: 'pending',
    DECLINED: 'declined',
};
export const CollaboratorSchema = z.object({
    userId: z.string().uuid(),
    name: z.string(),
    email: z.string().email().optional(),
    profilePictureUrl: z.string().url().nullable().optional(),
    role: z.enum(Object.values(CollaboratorRole)),
    status: z.enum(Object.values(CollaboratorStatus)),
    invitedAt: z.string().optional(),
    orgRole: z.enum(['admin', 'sales-rep']).optional(),
    isDeleted: z.boolean().optional(),
});
export const DealInvitationSchema = z.object({
    id: z.string().uuid(),
    dealId: z.string().uuid(),
    dealTitle: z.string(),
    dealValue: z.number().nullable(),
    dealStage: DealStageSchema,
    clientName: z.string(),
    invitedBy: z.object({
        userId: z.string().uuid(),
        name: z.string(),
        profilePictureUrl: z.string().url().nullable().optional(),
        orgRole: z.enum(['admin', 'sales-rep']).optional(),
    }),
    invitedAt: z.string().datetime({ offset: true }).or(z.string().datetime()),
});
export const UpdateDealStageRequestSchema = z.object({
    stage: DealStageSchema,
    reason: z.string().optional(),
    closeOutcome: DealCloseOutcomeSchema.optional(),
});
export const UpdateDealProbabilityRequestSchema = z.object({
    probability: z.number().min(0).max(100),
});
export const UpdateDealHealthRequestSchema = z.object({
    health: DealHealthSchema,
});
export const InviteCollaboratorRequestSchema = z.object({
    userId: z.string().uuid(),
});
export const CollaboratorsResponseSchema = z.object({
    collaborators: z.array(CollaboratorSchema),
    pendingInvites: z.array(CollaboratorSchema),
    declinedInvites: z.array(CollaboratorSchema),
});
export const DealDetailResponseSchema = z.object({
    deal: DealDetailSchema,
    timeline: z.array(TimelineItemSchema),
    collaborators: CollaboratorsResponseSchema,
    readOnly: z.boolean().optional(),
});
export const CreateDealKanbanRequestSchema = z.object({
    clientId: z.string().uuid(),
    name: z.string().min(1).max(255),
    value: z.number().nullable().optional(),
    projectedRevenue: z.number().nullable().optional(),
    expectedCloseDate: z.string().nullable().optional(),
    probability: z.number().min(0).max(100).nullable().optional(),
    assigneeId: z.string().optional(),
    productIds: z.array(z.string().uuid()).default([]),
});
// =============================================================================
// Deal Strategic Insights (AI-powered)
// =============================================================================
/** Types of strategic insights */
export const DealInsightType = {
    ACTION: 'action',
    WARNING: 'warning',
    OPPORTUNITY: 'opportunity',
};
export const DealInsightTypeSchema = z.enum(Object.values(DealInsightType));
/** Types of suggested actions */
export const SuggestedActionType = {
    EMAIL: 'email',
    MEETING: 'meeting',
    TASK: 'task',
};
export const SuggestedActionTypeSchema = z.enum(Object.values(SuggestedActionType));
export const DealInsightSuggestedActionSchema = z.object({
    type: SuggestedActionTypeSchema,
    label: z.string(),
    metadata: z.record(z.string(), z.unknown()).optional(),
});
export const DealStrategicInsightSchema = z.object({
    id: z.string(),
    type: DealInsightTypeSchema,
    title: z.string(),
    description: z.string(),
    metric: z.string().optional(),
    suggestedAction: DealInsightSuggestedActionSchema.optional(),
});
/** Actions Deal Pulse may offer for the selected next step. */
export const DealPulseActionType = {
    NONE: 'none',
    DRAFT_EMAIL: 'draft_email',
    SCHEDULE_CALL: 'schedule_call',
    OPEN_LATEST_MEETING: 'open_latest_meeting',
    FIND_ANOTHER_CONTACT: 'find_another_contact',
    UPDATE_CLOSE_DATE: 'update_close_date',
    COPY_RECAP: 'copy_recap',
    CREATE_ONBOARDING_TASK: 'create_onboarding_task',
    SCHEDULE_CHECK_IN: 'schedule_check_in',
    CONFIRM_LOSS_REASON: 'confirm_loss_reason',
};
export const DealPulseActionTypeSchema = z.enum(Object.values(DealPulseActionType));
export const DealPulseReferenceType = {
    ACTION_ITEM: 'action_item',
    MEETING: 'meeting',
    EMAIL: 'email',
};
export const DealPulseReferenceSchema = z.object({
    type: z.enum(Object.values(DealPulseReferenceType)),
    id: z.string().min(1),
    label: z.string().min(1),
    threadId: z.string().nullable().optional(),
}).strict();
export const DealPulseNextStepSchema = z.object({
    title: z.string().min(1).max(120),
    description: z.string().max(180),
    references: z.array(DealPulseReferenceSchema).max(5).optional(),
});
export const DealStrategicInsightsResponseSchema = z.object({
    insights: z.array(DealStrategicInsightSchema),
    generatedAt: z.string(),
    fromCache: z.boolean().optional(),
    accessLevel: z.enum(['collaborator', 'read_only', 'admin']).optional(),
    readOnly: z.boolean().optional(),
});
export const RelatedEmailThreadSchema = z.object({
    threadId: z.string(),
    subject: z.string().nullable(),
    snippet: z.string().nullable(),
    senderName: z.string().nullable(),
    senderEmail: z.string().email(),
    messageCount: z.number().int().positive(),
    latestSentAt: z.string(),
    isRecommended: z.boolean(),
    participantAvatars: z.array(z.string().url()).optional(),
});
export const RelatedThreadsResponseSchema = z.object({
    suggestedSubject: z.string(),
    threads: z.array(RelatedEmailThreadSchema),
});
